/* eslint-disable no-undefined */
/* eslint-disable max-statements */
/* eslint-disable max-lines-per-function */
import { useNavigate } from "react-router-dom";
import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { FormikHelpers } from "formik";
import {
  frequenciesAll,
  getInterestListsForSubscriptions,
  getAllPropertiesForSubscriptions,
  plansAllForSubscriptions,
  pricingAllForSubscriptions,
} from "types/gql-types";

import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { AdminHeading } from "../shared/admin-form";
import SubscriptionForm, {
  SubscriptionForm as SubscriptionFormType,
} from "./form";
import {
  CREATE_SUBSCRIPTION,
  GET_PROPERTIES_FOR_SUBSCRIPTIONS,
  GET_INTEREST_LISTS_FOR_SUBSCRIPTIONS,
  GET_PLANS_FOR_SUBSCRIPTIONS,
  GET_PRICING_FOR_SUBSCRIPTIONS,
  GET_FREQUENCIES,
} from "./queries";
import {
  applySubscriptionGraphqlFieldErrors,
  formatSubscriptionFrequencyOptions,
  formatSubscriptionPlanOptions,
  formatSubscriptionPricingOptions,
  interestListLabel,
  propertyLabel,
  subscriptionFormDateToString,
} from "./subscription-utils";

const initialData: SubscriptionFormType = {
  propertyId: undefined,
  interestListId: undefined,
  planId: undefined,
  pricingId: undefined,
  billingFrequencyId: undefined,
  startDate: null,
  endDate: null,
  renewalDate: null,
};

const NewSubscription: React.FC = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbar();
  const [sending, setSending] = useState(false);
  const [createSubscription] = useMutation(CREATE_SUBSCRIPTION, {
    refetchQueries: ["subscriptionsAll"],
  });

  const { data: propertiesData } = useQuery<getAllPropertiesForSubscriptions>(
    GET_PROPERTIES_FOR_SUBSCRIPTIONS,
    {
      variables: { includeArchived: false },
      fetchPolicy: "network-only",
    },
  );
  const { data: interestListsData } = useQuery<getInterestListsForSubscriptions>(
    GET_INTEREST_LISTS_FOR_SUBSCRIPTIONS,
    {
      variables: { includeArchived: false },
      fetchPolicy: "network-only",
    },
  );
  const { data: plansData } = useQuery<plansAllForSubscriptions>(GET_PLANS_FOR_SUBSCRIPTIONS, { fetchPolicy: "no-cache" });
  const { data: pricingData } = useQuery<pricingAllForSubscriptions>(GET_PRICING_FOR_SUBSCRIPTIONS, { fetchPolicy: "no-cache" });
  const { data: frequenciesData } = useQuery<frequenciesAll>(GET_FREQUENCIES, { fetchPolicy: "no-cache" });

  const submitForm = (
    formData: SubscriptionFormType,
    {
      setErrors,
      setTouched,
    }: Pick<FormikHelpers<SubscriptionFormType>, "setErrors" | "setTouched">,
  ) => {
    setSending(true);

    const cleanedData = {
      propertyId: formData.propertyId || "",
      interestListId: formData.interestListId || "",
      planId: formData.planId || "",
      pricingId: formData.pricingId || "",
      billingFrequencyId: formData.billingFrequencyId ?? 0,
      startDate: subscriptionFormDateToString(formData.startDate ?? null),
      endDate: subscriptionFormDateToString(formData.endDate ?? null),
      renewalDate: subscriptionFormDateToString(formData.renewalDate ?? null),
    };

    return createSubscription({
      variables: { data: cleanedData },
      refetchQueries: ["subscriptionsAll"],
      awaitRefetchQueries: true,
    })
      .then(() => {
        openSnackbar({
          message: "Subscription created successfully",
          status: "success",
        });
        navigate("/dash/admin/subscriptions");
      })
      .catch((err) => {
        logger.sendError(err);
        const message =
          err?.graphQLErrors?.[0]?.message ??
          err?.message ??
          "Failed to create subscription";
        if (!applySubscriptionGraphqlFieldErrors(message, { setErrors, setTouched })) {
          openSnackbar({
            message,
            status: "error",
          });
        }
      })
      .finally(() => {
        setSending(false);
      });
  };

  const properties = propertiesData?.properties ?? [];
  const interestLists = interestListsData?.interestLists ?? [];
  const plans = plansData?.plansAll ?? [];
  const pricingList = pricingData?.pricingAll ?? [];
  const frequencies = frequenciesData?.frequenciesAll ?? [];

  const propertyOptions = properties.map(
    (p) => ({
      value: p.id,
      label: propertyLabel(p),
    }),
  );

  const interestListOptions = interestLists.map(
    (il) => ({
      value: il.id,
      label: interestListLabel(il),
    }),
  );

  const planOptions = formatSubscriptionPlanOptions(plans);
  const pricingOptions = formatSubscriptionPricingOptions(pricingList);
  const frequencyOptions = formatSubscriptionFrequencyOptions(frequencies);

  return (
    <div>
      <AdminHeading>Create Subscription</AdminHeading>
      <SubscriptionForm
        formType="new"
        initialData={initialData}
        sending={sending}
        handleSubmit={submitForm}
        propertyOptions={propertyOptions}
        interestListOptions={interestListOptions}
        planOptions={planOptions}
        pricingOptions={pricingOptions}
        frequencyOptions={frequencyOptions}
      />
    </div>
  );
};

export default NewSubscription;
